import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email()
  .max(255);

async function getAdmin() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}

async function assertAdmin(userId: string) {
  const admin = await getAdmin();
  const { data } = await admin
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (!data) throw new Error("Forbidden");
}

/**
 * Public — anyone (guest or member) can ask to join the newsletter.
 *
 * DOUBLE OPT-IN: this never marks anyone as subscribed. The row is written in
 * a pending state (`marketing_consent = false`, no `consent_at`) and a signed
 * confirmation link is emailed. Only `confirmSubscription` — which requires
 * that signed token — records consent, so nobody can sign somebody else up.
 * The unique index on `email` means a repeat request updates the pending row
 * instead of creating a duplicate; an already-confirmed address reports
 * `duplicate: true` so the UI can say so honestly.
 */

export const subscribeEmail = createServerFn({ method: "POST" })
  .inputValidator(
    (input: {
      email: string;
      source?: string;
      marketing_consent?: boolean;
      utm_source?: string | null;
      utm_medium?: string | null;
      utm_campaign?: string | null;
      referrer?: string | null;
      landing_path?: string | null;
      platform?: string | null;
    }) =>
      z
        .object({
          email: emailSchema,
          source: z.string().trim().max(80).optional(),
          marketing_consent: z.boolean().optional(),
          utm_source: z.string().trim().max(120).nullish(),
          utm_medium: z.string().trim().max(120).nullish(),
          utm_campaign: z.string().trim().max(160).nullish(),
          referrer: z.string().trim().max(500).nullish(),
          landing_path: z.string().trim().max(300).nullish(),
          platform: z.string().trim().max(80).nullish(),
        })
        .parse(input),
  )
  .handler(async ({ data }) => {
    const admin = await getAdmin();
    const now = new Date().toISOString();
    const consent = data.marketing_consent ?? true;

    const { data: existing } = await admin
      .from("email_subscribers")
      .select(
        "id, marketing_consent, confirmed_at, unsubscribed_at, utm_source, referrer, landing_path, platform",
      )
      .eq("email", data.email)
      .maybeSingle();

    // Already confirmed and still on the list — say so, send nothing.
    const duplicate =
      !!existing &&
      !!existing.confirmed_at &&
      existing.marketing_consent &&
      !existing.unsubscribed_at;

    const { error } = await admin
      .from("email_subscribers")
      .upsert(
        {
          email: data.email,
          source: data.source ?? "website",
          // Pending until the signed confirmation link is clicked. An already
          // confirmed subscriber keeps their consent untouched.
          marketing_consent: duplicate ? true : false,
          confirmed_at: duplicate ? existing!.confirmed_at : null,
          confirmation_sent_at: duplicate ? undefined : now,
          last_activity_at: now,
          // Consent is only recorded at confirmation time (GDPR / PECR evidence).
          consent_at: duplicate ? undefined : null,
          consent_source: data.source ?? "website",
          unsubscribed_at: null,
          // Keep the first-touch attribution when we already have it.
          utm_source: existing?.utm_source ?? data.utm_source ?? null,
          utm_medium: data.utm_medium ?? undefined,
          utm_campaign: data.utm_campaign ?? undefined,
          referrer: existing?.referrer ?? data.referrer ?? null,
          landing_path: existing?.landing_path ?? data.landing_path ?? null,
          platform: existing?.platform ?? data.platform ?? null,
        },
        { onConflict: "email" },
      );
    if (error) throw new Error(error.message);

    // Confirmation email: sent whenever the address isn't already confirmed.
    // Failures are swallowed inside the helper — the request must still succeed.
    if (consent && !duplicate) {
      const { sendNewsletterConfirmEmail } = await import(
        "@/lib/email/newsletter-confirm.server"
      );
      await sendNewsletterConfirmEmail({ email: data.email });
    }

    return { ok: true, duplicate, pending: !duplicate };
  });

/**
 * Public — completes double opt-in. Requires the signed HMAC token from the
 * confirmation email, so an address can only be subscribed by whoever can read
 * that inbox. Idempotent: a second click reports `already: true`.
 */
export const confirmSubscription = createServerFn({ method: "POST" })
  .inputValidator((input: { email: string; token: string }) =>
    z.object({ email: emailSchema, token: z.string().trim().max(128) }).parse(input),
  )
  .handler(async ({ data }) => {
    const { verifyConfirmToken } = await import(
      "@/lib/email/broadcast-template.server"
    );
    if (!verifyConfirmToken(data.email, data.token)) {
      return { ok: false as const, already: false };
    }

    const admin = await getAdmin();
    const now = new Date().toISOString();

    const { data: existing } = await admin
      .from("email_subscribers")
      .select("id, confirmed_at, marketing_consent, unsubscribed_at, consent_source")
      .eq("email", data.email)
      .maybeSingle();

    if (!existing) return { ok: false as const, already: false };

    const already =
      !!existing.confirmed_at && existing.marketing_consent && !existing.unsubscribed_at;

    if (!already) {
      const { error } = await admin
        .from("email_subscribers")
        .update({
          marketing_consent: true,
          confirmed_at: existing.confirmed_at ?? now,
          consent_at: now,
          unsubscribed_at: null,
          last_activity_at: now,
        })
        .eq("id", existing.id);
      if (error) throw new Error(error.message);

      const { sendNewsletterWelcomeEmail } = await import(
        "@/lib/email/newsletter-welcome.server"
      );
      await sendNewsletterWelcomeEmail({ email: data.email, consentAt: now });

      // Attribute the recovery if this person had been sent the one-time
      // confirmation reminder.
      const { markReminderConfirmed } = await import("@/lib/newsletter-reminder.server");
      await markReminderConfirmed(data.email);
    }

    return { ok: true as const, already };
  });


/**
 * Public — requests an unsubscribe *confirmation link* for an address.
 *
 * SECURITY: this endpoint deliberately performs no mutation. Consent state is
 * only changed by `performUnsubscribeFn`, which requires a signed HMAC token
 * (or a stored per-recipient token), so nobody can opt somebody else out just
 * by knowing their address. The response is identical whether or not the
 * address exists, to avoid subscriber enumeration.
 */
export const requestUnsubscribeLink = createServerFn({ method: "POST" })
  .inputValidator((input: { email: string }) =>
    z.object({ email: emailSchema }).parse(input),
  )
  .handler(async ({ data }) => {
    try {
      const { unsubscribeUrl } = await import(
        "@/lib/email/broadcast-template.server"
      );
      const { sendEmail, MEMBER_FROM } = await import(
        "@/lib/email/resend.server"
      );
      const link = unsubscribeUrl(data.email);
      await sendEmail({
        from: MEMBER_FROM,
        to: data.email,
        subject: "Confirm your unsubscribe · Recovery Trauma™",
        html: `<p>We received a request to unsubscribe this address from Recovery Trauma™ marketing emails.</p>
<p><a href="${link}">Confirm unsubscribe</a></p>
<p>If you didn't ask for this, you can safely ignore this email — nothing has changed.</p>`,
      });
    } catch {
      // Never reveal delivery/lookup outcomes to the caller.
    }
    return { ok: true };
  });

export const listSubscribers = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (
      input: {
        search?: string;
        accountType?: "all" | "guest" | "free" | "paid";
        since?: string;
      } = {},
    ) =>
      z
        .object({
          search: z.string().max(120).optional(),
          accountType: z.enum(["all", "guest", "free", "paid"]).optional(),
          since: z.string().datetime().optional(),
        })
        .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const admin = await getAdmin();

    let q = admin
      .from("email_subscribers")
      .select("id, email, source, marketing_consent, user_id, last_activity_at, created_at")
      .order("created_at", { ascending: false })
      .limit(500);
    if (data.search) q = q.ilike("email", `%${data.search}%`);
    if (data.since) q = q.gte("created_at", data.since);
    const { data: subs, error } = await q;
    if (error) throw new Error(error.message);

    const userIds = (subs ?? []).map((s) => s.user_id).filter(Boolean) as string[];
    let profileMap = new Map<string, { membership_tier: string | null }>();
    if (userIds.length) {
      const { data: profiles } = await admin
        .from("profiles")
        .select("user_id, membership_tier")
        .in("user_id", userIds);
      profileMap = new Map((profiles ?? []).map((p) => [p.user_id, p]));
    }

    const enriched = (subs ?? []).map((s) => {
      const tier = s.user_id ? profileMap.get(s.user_id)?.membership_tier ?? "free" : null;
      const accountType: "guest" | "free" | "paid" = !s.user_id
        ? "guest"
        : tier === "healing"
          ? "paid"
          : "free";
      return { ...s, account_type: accountType, membership_tier: tier };
    });

    const filtered =
      data.accountType && data.accountType !== "all"
        ? enriched.filter((s) => s.account_type === data.accountType)
        : enriched;

    const totals = {
      total: enriched.length,
      guest: enriched.filter((s) => s.account_type === "guest").length,
      free: enriched.filter((s) => s.account_type === "free").length,
      paid: enriched.filter((s) => s.account_type === "paid").length,
    };

    return { subscribers: filtered, totals };
  });
